import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import UserProfile from '../components/UserProfile';
import { EngineeringLoadingScreen } from '../components/EngineeringLoadingScreen';
import { 
    getUserProfile, 
    getCompanyProfile, 
    getUserByEmail, 
    isUserInCompany, 
    getCompanyMembers, 
    CompanyMember, 
    updateUserRole, 
    updateMemberRoleInCompany, 
    removeMemberFromCompany, 
    addUserToCompany, 
    addEditorToCompany,
    updateUserOrganization,
    syncAuthTokenClaimsForUser,
    hasActiveSubscription,
    checkManagerSubscriptionStatus,
    getSubscriptionLimits,
    LIMITS_VERSION,
    addToolToCompany,
    addCustomToolToCompany,
    updateToolColor,
    updateToolMinPrecision,
    removeToolFromCompany,
    getStandardToolDefaultPrecisionClass,
    Tool
} from '../services/authService';
import { auth } from '../config/firebase';
import { Language } from '../constants/strings';
import { logger } from '../utils/logger';
import { syncSubscriptionStatus, verifyCheckoutSession, createCheckoutSession } from '../services/stripeService';
import { useSearchParams } from 'react-router-dom';
import '../../public/styles/main.css';

interface DashboardPageProps {
    onLogout: () => void;
}

export default function DashboardPage({ onLogout }: DashboardPageProps) {
    const { strings, language, setLanguage } = useLanguage();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [userFirebaseRole, setUserFirebaseRole] = useState<string>('');
    const [userDisplayName, setUserDisplayName] = useState<string>('');
    const [isLoadingRole, setIsLoadingRole] = useState(true);
    const [searchEmail, setSearchEmail] = useState('');
    const [emailError, setEmailError] = useState('');
    const [emailSuccess, setEmailSuccess] = useState('');
    const [editorLimit, setEditorLimit] = useState<number>(1);
    const [userLimit, setUserLimit] = useState<number>(5);
    const [toolLimit, setToolLimit] = useState<number | 'unlimited'>(3);
    const [currentEditors, setCurrentEditors] = useState<number>(0);
    const [currentUsers, setCurrentUsers] = useState<number>(0);
    const [companyName, setCompanyName] = useState<string>('');
    const [isSendingInvitation, setIsSendingInvitation] = useState(false);
    const [companyMembers, setCompanyMembers] = useState<CompanyMember[]>([]);
    const [isLoadingMembers, setIsLoadingMembers] = useState(false);
    const [editingMember, setEditingMember] = useState<string | null>(null);
    const [editedRoles, setEditedRoles] = useState<{ [key: string]: 'user' | 'editor' }>({});
    const [deletingMember, setDeletingMember] = useState<{ uid: string; email: string } | null>(null);
    const [hasActiveSub, setHasActiveSub] = useState<boolean>(false);
    const [subscriptionStatus, setSubscriptionStatus] = useState<string>('');
    const [companyTools, setCompanyTools] = useState<Tool[]>([]);
    const [isLoadingTools, setIsLoadingTools] = useState(false);
    const [showAddToolDropdown, setShowAddToolDropdown] = useState(false);
    const [showAddCustomToolModal, setShowAddCustomToolModal] = useState(false);
    const [customToolName, setCustomToolName] = useState('');
    const [customToolImageFile, setCustomToolImageFile] = useState<File | null>(null);
    const [customToolImagePreview, setCustomToolImagePreview] = useState<string | null>(null);
    const [customToolColor, setCustomToolColor] = useState<string>('#2563EB');
    const [customToolWithoutMeasurement, setCustomToolWithoutMeasurement] = useState<boolean>(false);
    const [customToolPrecisionClass, setCustomToolPrecisionClass] = useState<string>('');
    const [customToolMinimumPrecision, setCustomToolMinimumPrecision] = useState<string>('');
    const [standardToolPrecisionClass, setStandardToolPrecisionClass] = useState<string>('');
    const [toolError, setToolError] = useState('');
    const [showAddToolConfirm, setShowAddToolConfirm] = useState(false);
    const [pendingToolNumber, setPendingToolNumber] = useState<number | null>(null);
    const [showAddCustomToolConfirm, setShowAddCustomToolConfirm] = useState(false);
    const [editedToolColors, setEditedToolColors] = useState<{ [toolId: string]: string }>({});
    const [hasColorChanges, setHasColorChanges] = useState(false);
    const [deletingTool, setDeletingTool] = useState<{ id: string; name: string } | null>(null);
    const [showDeleteToolConfirm, setShowDeleteToolConfirm] = useState(false);
    const [isProcessingToolPayment, setIsProcessingToolPayment] = useState(false);
    const [openMinPrecisionDropdown, setOpenMinPrecisionDropdown] = useState<string | null>(null);

    // Auto-clear error message after 5 seconds
    useEffect(() => {
        if (!emailError) {
            return;
        }

        const timer = setTimeout(() => {
            setEmailError('');
        }, 5000);

        return () => {
            clearTimeout(timer);
        };
    }, [emailError]);

    // Auto-clear success message after 5 seconds
    useEffect(() => {
        if (!emailSuccess) {
            return;
        }

        const timer = setTimeout(() => {
            setEmailSuccess('');
        }, 5000);

        return () => {
            clearTimeout(timer);
        };
    }, [emailSuccess]);

    // Auto-clear tool error message after 5 seconds
    useEffect(() => {
        if (!toolError) {
            return;
        }

        const timer = setTimeout(() => {
            setToolError('');
        }, 5000);

        return () => {
            clearTimeout(timer);
        };
    }, [toolError]);

    // Close tool dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            if (showAddToolDropdown && !target.closest('.tools-add-container')) {
                setShowAddToolDropdown(false);
            }
            if (openMinPrecisionDropdown && !target.closest('.min-precision-dropdown-container')) {
                setOpenMinPrecisionDropdown(null);
            }
        };

        if (showAddToolDropdown || openMinPrecisionDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showAddToolDropdown, openMinPrecisionDropdown]);

    // Cache dashboard data for fast reload when navigating back
    useEffect(() => {
        const currentUser = auth.currentUser;
        if (!currentUser || isLoadingRole || !userFirebaseRole) {
            return;
        }

        if (userFirebaseRole.toLowerCase() === 'manager' && isLoadingMembers) {
            return;
        }

        if (userFirebaseRole && userDisplayName) {
            const cacheKey = `dashboard_cache_${currentUser.uid}`;
            const cacheData = {
                timestamp: Date.now(),
                limitsVersion: LIMITS_VERSION,
                userFirebaseRole,
                userDisplayName,
                companyName,
                hasActiveSub,
                subscriptionStatus,
                editorLimit,
                userLimit,
                toolLimit,
                currentEditors,
                currentUsers,
                companyMembers,
                companyTools,
                language: language || 'et',
                subscriptionId: currentUser.uid ? (() => {
                    // Try to get subscriptionId from current user profile
                    // This will be updated when we load the profile
                    return null;
                })() : null
            };
            sessionStorage.setItem(cacheKey, JSON.stringify(cacheData));
        }
    }, [userFirebaseRole, userDisplayName, companyName, hasActiveSub, subscriptionStatus, 
        editorLimit, userLimit, toolLimit, currentEditors, currentUsers, companyMembers, companyTools, isLoadingRole, isLoadingMembers, language]);

    // Load user language from profile on component mount
    useEffect(() => {
        const loadUserLanguage = async () => {
            const currentUser = auth.currentUser;
            if (currentUser) {
                try {
                    const userProfile = await getUserProfile(currentUser.uid);
                    if (userProfile && userProfile.language) {
                        const userLanguage = userProfile.language as Language;
                        if (userLanguage === 'en' || userLanguage === 'et' || userLanguage === 'ru') {
                            setLanguage(userLanguage);
                            logger.log('[DashboardPage] Language set from user profile:', userLanguage);
                        }
                    }
                } catch (error) {
                    logger.error('[DashboardPage] Error loading user language:', error);
                }
            }
        };
        loadUserLanguage();
    }, [setLanguage]);

    // Load user role from Firebase on component mount
    useEffect(() => {
        let mounted = true;
        
        const loadUserRole = async () => {
            try {
                const currentUser = auth.currentUser;
                if (!currentUser) {
                    logger.warn('No current user found');
                    navigate('/login', { replace: true });
                    return;
                }

                // Check for cached data in sessionStorage
                const cacheKey = `dashboard_cache_${currentUser.uid}`;
                const cachedData = sessionStorage.getItem(cacheKey);
                const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
                let useCache = false;
                
                if (cachedData) {
                    try {
                        const parsed = JSON.parse(cachedData);
                        const cacheAge = Date.now() - parsed.timestamp;
                        const cacheValid = cacheAge < CACHE_DURATION && parsed.limitsVersion === LIMITS_VERSION;
                        
                        if (cacheValid) {
                            logger.log('[Dashboard] Using cached data, age:', Math.round(cacheAge / 1000), 'seconds');
                            setUserFirebaseRole(parsed.userFirebaseRole);
                            setUserDisplayName(parsed.userDisplayName);
                            setCompanyName(parsed.companyName || '');
                            setHasActiveSub(parsed.hasActiveSub);
                            setSubscriptionStatus(parsed.subscriptionStatus || '');
                            setEditorLimit(parsed.editorLimit ?? 1);
                            setUserLimit(parsed.userLimit ?? 5);
                            setToolLimit(parsed.toolLimit ?? 3);
                            setCurrentEditors(parsed.currentEditors ?? 0);
                            setCurrentUsers(parsed.currentUsers ?? 0);
                            setCompanyMembers(parsed.companyMembers || []);
                            setCompanyTools(parsed.companyTools || []);
                            setIsLoadingMembers(false);
                            
                            setIsLoadingRole(false);
                            useCache = true;
                        } else {
                            logger.log('[Dashboard] Cache expired, loading fresh data');
                            sessionStorage.removeItem(cacheKey);
                        }
                    } catch (e) {
                        logger.warn('[Dashboard] Error parsing cached data:', e);
                        sessionStorage.removeItem(cacheKey);
                    }
                }
                
                // Wait a bit for auth state to settle
                await new Promise(resolve => setTimeout(resolve, 100));
                
                if (!mounted) return;
                
                const userProfile = await getUserProfile(currentUser.uid);
                    
                    if (!mounted) return;
                    
                    if (userProfile && userProfile.role) {
                        // Store original Firebase role and display name
                        setUserFirebaseRole(userProfile.role);
                        setUserDisplayName(userProfile.displayName || currentUser.displayName || 'User');
                        
                        // Check subscription status (using cached data from login sync)
                        let isActive = hasActiveSubscription(userProfile);
                        const currentStatus = userProfile.subscriptionStatus || null;
                        setSubscriptionStatus(currentStatus || '');
                        setHasActiveSub(isActive);
                        
                        logger.log('[Dashboard] Manager subscription check:', {
                            role: userProfile.role,
                            subscriptionId: userProfile.subscriptionId,
                            subscriptionStatus: currentStatus,
                            isActive: isActive
                        });
                        
                        // Note: Subscription sync happens only on login (checkpoint)
                        // During session, we use cached data from login sync
                        
                        // Load subscription limits (computed from plan; Ultimate plan supports overrides via Firebase Console)
                        const limits = getSubscriptionLimits(userProfile.selectedPlan, {
                            editorLimitOverride: userProfile.editorLimitOverride,
                            userLimitOverride: userProfile.userLimitOverride,
                            toolLimitOverride: userProfile.toolLimitOverride
                        });
                        setEditorLimit(limits.editorLimit);
                        setUserLimit(limits.userLimit);
                        setToolLimit(limits.toolLimit);
                        
                        const role = userProfile.role.toLowerCase();

                        if (role === 'admin' || role === 'manager') {
                            logger.log('Admin/Manager role detected - dashboard access granted');
                            if (!useCache) {
                                setIsLoadingRole(false);
                            }

                            if (role === 'manager') {
                                const companyProfile = await getCompanyProfile(currentUser.uid);
                                if (companyProfile && mounted) {
                                    setCompanyName(companyProfile.companyName);
                                    setCompanyTools(companyProfile.tools || []);

                                    if (!useCache) {
                                        setIsLoadingMembers(true);
                                    }

                                    try {
                                        const members = await getCompanyMembers(currentUser.uid);
                                        if (mounted) {
                                            setCompanyMembers(members);
                                            const editorsCount = members.filter(m => m.role === 'editor').length;
                                            const usersCount = members.filter(m => m.role === 'user').length;
                                            setCurrentEditors(editorsCount);
                                            setCurrentUsers(usersCount);
                                            logger.log(`Editors: ${editorsCount}/${limits.editorLimit}, Users: ${usersCount}/${limits.userLimit}`);
                                        }
                                    } catch (memberError) {
                                        logger.error('Error loading company members:', memberError);
                                    } finally {
                                        if (mounted) {
                                            setIsLoadingMembers(false);
                                        }
                                    }
                                }
                            }
                        } else if (role === 'user' || role === 'editor') {
                            // Redirect user/editor to home
                            logger.log('User/Editor detected - redirecting to home');
                            navigate('/home', { replace: true });
                        } else {
                            logger.warn('Unknown role from Firebase:', userProfile.role);
                            navigate('/login', { replace: true });
                        }
                    } else {
                        logger.warn('No user profile found');
                        navigate('/login', { replace: true });
                    }
            } catch (error) {
                logger.error('Error loading user role from Firebase:', error);
                if (mounted) {
                    navigate('/login', { replace: true });
                }
            }
        };

        loadUserRole();
        
        // Cleanup function
        return () => {
            mounted = false;
        };
    }, [navigate]);

    // Handle return from Stripe checkout for tool deletion
    useEffect(() => {
        const sessionId = searchParams.get('session_id');
        
        if (sessionId) {
            logger.log('[Dashboard] Payment callback detected:', { sessionId });
            
            setIsProcessingToolPayment(true);
            
            verifyCheckoutSession(sessionId).then(async (verificationResult) => {
                logger.log('[Dashboard] Verification result:', verificationResult);
                
                if (!verificationResult.success || !verificationResult.session) {
                    logger.error('[Dashboard] Payment verification failed:', verificationResult);
                    setIsProcessingToolPayment(false);
                    // Remove session_id from URL
                    navigate('/dashboard', { replace: true });
                    return;
                }
                
                const paymentStatus = verificationResult.session.paymentStatus;
                logger.log('[Dashboard] Payment status:', paymentStatus);
                
                // Check if payment was successful
                if (paymentStatus === 'paid' || paymentStatus === 'complete') {
                    // Check if this is remove_single_tool action
                    const action = searchParams.get('action');
                    const toolId = searchParams.get('toolId');
                    
                    if (action === 'remove_single_tool' && toolId && auth.currentUser) {
                        logger.log('[Dashboard] Remove single tool payment successful, removing tool...');
                        try {
                            await removeToolFromCompany(auth.currentUser.uid, toolId);
                            
                            // Reload company profile to get updated tools
                            const updatedProfile = await getCompanyProfile(auth.currentUser.uid);
                            if (updatedProfile) {
                                setCompanyTools(updatedProfile.tools || []);
                            }
                            
                            logger.log('[Dashboard] Tool removed successfully');
                        } catch (error: any) {
                            logger.error('[Dashboard] Error removing tool:', error);
                            setToolError(error.message || strings.DASHBOARD_REMOVE_TOOL_ERROR);
                        } finally {
                            setIsProcessingToolPayment(false);
                            // Remove session_id and action from URL
                            navigate('/dashboard', { replace: true });
                        }
                        return;
                    }
                } else {
                    // Payment was not completed
                    logger.warn('[Dashboard] Payment was not completed');
                    setIsProcessingToolPayment(false);
                    // Remove session_id from URL
                    navigate('/dashboard', { replace: true });
                }
            }).catch((error) => {
                logger.error('[Dashboard] Error verifying payment:', error);
                setIsProcessingToolPayment(false);
                // Remove session_id from URL
                navigate('/dashboard', { replace: true });
            });
        }
    }, [searchParams, navigate]);

    const validateEmail = (email: string): boolean => {
        // Strict email validation regex pattern
        // Local part: letters, numbers, dots, hyphens, underscores (no dot at start/end, no consecutive dots)
        // Domain part: letters, numbers, hyphens, then dot, then 2+ letters for extension
        const emailRegex = /^[a-zA-Z0-9]+([._-][a-zA-Z0-9]+)*@[a-zA-Z0-9]+([.-][a-zA-Z0-9]+)*\.[a-zA-Z]{2,}$/;
        
        // Additional checks
        if (!emailRegex.test(email)) {
            return false;
        }
        
        // Check for consecutive dots
        if (email.includes('..')) {
            return false;
        }
        
        // Check that local part doesn't start or end with dot
        const [localPart, domainPart] = email.split('@');
        if (localPart.startsWith('.') || localPart.endsWith('.')) {
            return false;
        }
        
        // Check that domain part doesn't start or end with dot or hyphen
        if (domainPart.startsWith('.') || domainPart.startsWith('-') || 
            domainPart.endsWith('.') || domainPart.endsWith('-')) {
            return false;
        }
        
        return true;
    };

    const handleAddEmail = async () => {
        const email = searchEmail.trim().toLowerCase();
        
        if (!email) {
            setEmailError(strings.DASHBOARD_ENTER_EMAIL);
            setEmailSuccess('');
            return;
        }
        
        if (!validateEmail(email)) {
            setEmailError(strings.DASHBOARD_INVALID_EMAIL);
            setEmailSuccess('');
            return;
        }
        
        const currentUser = auth.currentUser;
        if (!currentUser) {
            setEmailError(strings.DASHBOARD_USER_NOT_LOGGED_IN);
            setEmailSuccess('');
            return;
        }

        setIsSendingInvitation(true);
        setEmailError('');
        setEmailSuccess('');

        try {
            // Check if user already belongs to this company
            const userInCompany = await isUserInCompany(currentUser.uid, email);
            if (userInCompany) {
                setEmailError(strings.DASHBOARD_USER_ALREADY_IN_COMPANY);
                setIsSendingInvitation(false);
                return;
            }
            
            // Load user by email
            const existingUser = await getUserByEmail(email);
            
            if (!existingUser) {
                // User does not exist in system
                setEmailError(strings.DASHBOARD_ADD_EMAIL_ERROR);
                setIsSendingInvitation(false);
                return;
            }

            // If user already has organization set
            // do not allow automatic assignment to another company
            const hasOrganization =
                typeof existingUser.organization === 'string' &&
                existingUser.organization.trim() !== '';

            if (hasOrganization) {
                setEmailError(strings.DASHBOARD_ADD_EMAIL_ERROR);
                setIsSendingInvitation(false);
                return;
            }
            
            const userRole = (existingUser.role || '').toLowerCase();
            
            if (userRole === 'manager') {
                setEmailError(strings.DASHBOARD_ADD_EMAIL_ERROR);
                setIsSendingInvitation(false);
                return;
            }

            // Determine role for this company based on user profile
            const companyRole: 'user' | 'editor' = userRole === 'editor' ? 'editor' : 'user';

            // Check if subscription is active
            if (!hasActiveSub) {
                setEmailError(strings.SUBSCRIPTION_INACTIVE_ERROR_ADD);
                setIsSendingInvitation(false);
                return;
            }

            // Check subscription limits based on role
            if (companyRole === 'editor') {
                const futureEditorsCount = currentEditors + 1;
                if (futureEditorsCount > editorLimit) {
                    setEmailError(`${strings.DASHBOARD_EDITOR_LIMIT_REACHED} (${editorLimit})`);
                    setIsSendingInvitation(false);
                    return;
                }
            } else {
                const futureUsersCount = currentUsers + 1;
                if (futureUsersCount > userLimit) {
                    setEmailError(`${strings.DASHBOARD_USER_LIMIT_REACHED} (${userLimit})`);
                    setIsSendingInvitation(false);
                    return;
                }
            }

            // Add existing user to company based on role
            if (companyRole === 'editor') {
                await addEditorToCompany(currentUser.uid, existingUser.uid);
            } else {
                await addUserToCompany(currentUser.uid, existingUser.uid);
            }

            // If user had no organization assigned, set it to companyId (managerId)
            if (!hasOrganization) {
                await updateUserOrganization(existingUser.uid, currentUser.uid);
            } else {
                try {
                    await syncAuthTokenClaimsForUser(existingUser.uid);
                } catch (syncError) {
                    logger.warn('Claims sync after adding user failed', syncError);
                }
            }

            // Reload company members after successful add
            const updatedMembers = await getCompanyMembers(currentUser.uid);
            setCompanyMembers(updatedMembers);

            // Update counters
            const editorsCount = updatedMembers.filter(m => m.role === 'editor').length;
            const usersCount = updatedMembers.filter(m => m.role === 'user').length;
            setCurrentEditors(editorsCount);
            setCurrentUsers(usersCount);

            setEmailSuccess(strings.DASHBOARD_USER_ADDED_SUCCESS);
            setSearchEmail('');
            logger.log('User added to company:', email, 'role:', companyRole);
        } catch (error) {
            logger.error('Error adding user to company:', error);
            setEmailError(strings.DASHBOARD_ADD_USER_ERROR);
        } finally {
            setIsSendingInvitation(false);
        }
    };

    const handleEmailInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchEmail(e.target.value);
        // Clear messages when user starts typing
        setEmailError('');
        setEmailSuccess('');
    };

    const handleRoleEdit = (memberId: string, currentRole: 'user' | 'editor') => {
        setEditingMember(memberId);
        setEditedRoles({ ...editedRoles, [memberId]: currentRole });
    };

    const handleRoleChange = (memberId: string, newRole: 'user' | 'editor') => {
        setEditedRoles({ ...editedRoles, [memberId]: newRole });
    };

    const handleSaveRole = async (memberId: string) => {
        const newRole = editedRoles[memberId];
        if (!newRole) return;

        const currentUser = auth.currentUser;
        if (!currentUser) {
            logger.error('No current user');
            return;
        }

        // Find the member to get their current role
        const member = companyMembers.find(m => m.uid === memberId);
        if (!member) {
            logger.error('Member not found');
            return;
        }

        const oldRole = member.role;

        // If role hasn't changed, just close editing
        if (oldRole === newRole) {
            setEditingMember(null);
            return;
        }

        // Check if subscription is active
        if (!hasActiveSub) {
            setEmailError(strings.SUBSCRIPTION_INACTIVE_ERROR_ROLE);
            setEditingMember(null);
            return;
        }

        // Check subscription limits when upgrading to editor
        if (newRole === 'editor' && oldRole === 'user') {
            const futureEditorsCount = companyMembers.filter(m => 
                m.role === 'editor' || (m.uid === memberId)
            ).length;
            
            if (futureEditorsCount > editorLimit) {
                setEmailError(`${strings.DASHBOARD_EDITOR_LIMIT_REACHED} (${editorLimit})`);
                setEditingMember(null);
                return;
            }
        }

        // Check subscription limits when downgrading to user
        if (newRole === 'user' && oldRole === 'editor') {
            const futureUsersCount = companyMembers.filter(m => 
                m.role === 'user' || (m.uid === memberId)
            ).length;
            
            if (futureUsersCount > userLimit) {
                setEmailError(`${strings.DASHBOARD_USER_LIMIT_REACHED} (${userLimit})`);
                setEditingMember(null);
                return;
            }
        }

        try {
            // Update role in user's profile document
            await updateUserRole(memberId, newRole);
            
            // Update role in company document (move between editors and users arrays)
            await updateMemberRoleInCompany(currentUser.uid, memberId, oldRole, newRole);
            
            // Update member role in the local state
            const updatedMembers = companyMembers.map(m => 
                m.uid === memberId ? { ...m, role: newRole } : m
            );
            setCompanyMembers(updatedMembers);
            
            // Update counters
            const editorsCount = updatedMembers.filter(m => m.role === 'editor').length;
            const usersCount = updatedMembers.filter(m => m.role === 'user').length;
            setCurrentEditors(editorsCount);
            setCurrentUsers(usersCount);
            
            setEditingMember(null);
            setEmailError(''); // Clear any previous errors
            logger.log(`Role updated for ${memberId}: ${oldRole} -> ${newRole}`);
        } catch (error) {
            logger.error('Error updating role:', error);
            setEmailError(strings.DASHBOARD_UPDATE_ROLE_ERROR);
        }
    };

    const handleDeleteMember = (memberId: string) => {
        const member = companyMembers.find(m => m.uid === memberId);
        if (member) {
            setDeletingMember({ uid: member.uid, email: member.email });
        }
    };

    const confirmDeleteMember = async () => {
        if (!deletingMember) return;

        const currentUser = auth.currentUser;
        if (!currentUser) {
            logger.error('No current user');
            setDeletingMember(null);
            return;
        }

        // Find the member to get their role
        const member = companyMembers.find(m => m.uid === deletingMember.uid);
        if (!member) {
            logger.error('Member not found');
            setDeletingMember(null);
            return;
        }

        try {
            // Set global user role back to "user" before removing from company
            await updateUserRole(deletingMember.uid, 'user');

            // Clear organization field for this user in their profile
            // Do this BEFORE removing the user from company, so security rules still see
            // the user as a member of this manager's company
            await updateUserOrganization(deletingMember.uid, null);

            // Remove member from Firestore company arrays
            await removeMemberFromCompany(currentUser.uid, deletingMember.uid, member.role);
            
            // Remove member from local state
            const updatedMembers = companyMembers.filter(m => m.uid !== deletingMember.uid);
            setCompanyMembers(updatedMembers);
            
            // Update counters
            const editorsCount = updatedMembers.filter(m => m.role === 'editor').length;
            const usersCount = updatedMembers.filter(m => m.role === 'user').length;
            setCurrentEditors(editorsCount);
            setCurrentUsers(usersCount);
            
            logger.log(`Member deleted: ${deletingMember.uid} (${member.role}) and organization cleared`);
        } catch (error) {
            logger.error('Error deleting member:', error);
            setEmailError(strings.DASHBOARD_REMOVE_USER_ERROR);
        } finally {
            setDeletingMember(null);
        }
    };

    const cancelDeleteMember = () => {
        setDeletingMember(null);
    };

    // Helper function to get tool name by number
    const getToolName = (toolNumber: number): string => {
        switch (toolNumber) {
            case 1:
                return strings.CALIBER_TOOL;
            case 2:
                return strings.MICROMETER;
            case 3:
                return strings.DIGITAL_INSIDE_MICROMETER;
            case 4:
                return strings.STOPWATCH;
            case 5:
                return strings.HEIGHT_MEASURING_JACK;
            case 6:
                return strings.SCREW_DRIVER;
            case 7:
                return strings.TOOL_7 || `Tööriist 7`;
            case 8:
                return strings.TOOL_8 || `Tööriist 8`;
            case 9:
                return strings.TOOL_9 || `Tööriist 9`;
            case 10:
                return strings.TOOL_10 || `Tööriist 10`;
            case 11:
                return strings.TOOL_11 || `Tööriist 11`;
            case 12:
                return strings.MULTIMETER || 'Multimeeter';
            default:
                return `Tööriist ${toolNumber}`;
        }
    };

    // Helper function to get tool image path by number
    const getToolImagePath = (toolNumber: number): string => {
        switch (toolNumber) {
            case 1:
                return '/assets/measurement-tools/6.png';
            case 2:
                return '/assets/measurement-tools/5.png';
            case 3:
                return '/assets/measurement-tools/1.png';
            case 4:
                return '/assets/measurement-tools/2.png';
            case 5:
                return '/assets/measurement-tools/3.png';
            case 6:
                return '/assets/measurement-tools/4.png';
            case 7:
                return '/assets/measurement-tools/7.png';
            case 8:
                return '/assets/measurement-tools/8.png';
            case 9:
                return '/assets/measurement-tools/9.png';
            case 10:
                return '/assets/measurement-tools/10.png';
            case 11:
                return '/assets/measurement-tools/11.png';
            case 12:
                return '/assets/measurement-tools/multimeter.png';
            default:
                return '';
        }
    };

    // Helper function to get tool color by number (according to legend)
    const getToolColor = (toolNumber: number): string => {
        const legendColors = [
            '#2563EB', // 1 - blue
            '#DC2626', // 2 - red
            '#16A34A', // 3 - green
            '#7C3AED', // 4 - purple
            '#EA580C', // 5 - orange
            '#EAB308', // 6 - yellow
        ];
        
        // Use cyclic colors for all tools
        return legendColors[(toolNumber - 1) % legendColors.length];
    };

    // Helper function to add alpha channel to hex color (53% transparency to match PdfViewer)
    const addAlphaToHex = (hex: string, alpha: number = 0.53): string => {
        // Remove # if present
        const cleanHex = hex.replace('#', '');
        // Convert alpha to hex (0-255 range, 0.53 = 135 ≈ 88 in hex to match PdfViewer)
        const alphaHex = Math.round(alpha * 255).toString(16).padStart(2, '0');
        return `#${cleanHex}${alphaHex}`;
    };

    // Get available tools (tools not yet added)
    const getAvailableTools = (): number[] => {
        // All tools from assets: 1-11 plus multimeter (12)
        const allTools = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
        // Filter out tools that are already added (check by toolNumber for standard tools)
        const addedToolNumbers = companyTools
            .filter(tool => tool.type === 'standard' && tool.toolNumber)
            .map(tool => tool.toolNumber!);
        return allTools.filter(toolNumber => !addedToolNumbers.includes(toolNumber));
    };

    const handleAddTool = (toolNumber: number) => {
        const currentUser = auth.currentUser;
        if (!currentUser) {
            setToolError(strings.DASHBOARD_USER_NOT_LOGGED_IN);
            return;
        }

        // Check if subscription is active
        if (!hasActiveSub) {
            setToolError(strings.SUBSCRIPTION_INACTIVE_ERROR_ADD);
            return;
        }

        // Check tool limit
        if (toolLimit !== 'unlimited' && companyTools.length >= toolLimit) {
            setToolError(`${strings.DASHBOARD_TOOL_LIMIT_REACHED} (${toolLimit})`);
            return;
        }

        // Set default precision class value
        setStandardToolPrecisionClass(getStandardToolDefaultPrecisionClass(toolNumber));

        // Show confirmation modal
        setPendingToolNumber(toolNumber);
        setShowAddToolConfirm(true);
        setShowAddToolDropdown(false);
    };

    const handleCancelAddTool = () => {
        setShowAddToolConfirm(false);
        setPendingToolNumber(null);
        setStandardToolPrecisionClass('');
        setToolError('');
    };

    const handleConfirmAddTool = async () => {
        if (pendingToolNumber === null) {
            return;
        }

        const currentUser = auth.currentUser;
        if (!currentUser) {
            setToolError(strings.DASHBOARD_USER_NOT_LOGGED_IN);
            setShowAddToolConfirm(false);
            setPendingToolNumber(null);
            setStandardToolPrecisionClass('');
            return;
        }

        setIsLoadingTools(true);
        setToolError('');
        setShowAddToolConfirm(false);

        try {
            // Pass precisionClass value
            const precisionClassToUse = standardToolPrecisionClass.trim();
            
            await addToolToCompany(currentUser.uid, pendingToolNumber, precisionClassToUse);
            
            // Reload company profile to get updated tools
            const updatedProfile = await getCompanyProfile(currentUser.uid);
            if (updatedProfile) {
                setCompanyTools(updatedProfile.tools || []);
            }
            
            setStandardToolPrecisionClass('');
            setPendingToolNumber(null);
            logger.log(`Tool ${pendingToolNumber} added to company`);
        } catch (error: any) {
            logger.error('Error adding tool to company:', error);
            setToolError(error.message || strings.DASHBOARD_ADD_TOOL_ERROR);
            setShowAddToolConfirm(true);
        } finally {
            setIsLoadingTools(false);
        }
    };

    const handleAddCustomTool = () => {
        const currentUser = auth.currentUser;
        if (!currentUser) {
            setToolError(strings.DASHBOARD_USER_NOT_LOGGED_IN);
            return;
        }

        if (!customToolName.trim()) {
            setToolError(strings.DASHBOARD_TOOL_NAME_REQUIRED);
            return;
        }

        if (!customToolColor || customToolColor.trim() === '') {
            setToolError(strings.DASHBOARD_TOOL_COLOR_REQUIRED);
            return;
        }

        if (!customToolImageFile) {
            setToolError(strings.DASHBOARD_TOOL_IMAGE_REQUIRED);
            return;
        }

        if (!customToolPrecisionClass.trim()) {
            setToolError(strings.DASHBOARD_PRECISION_CLASS_REQUIRED);
            return;
        }

        // Check if subscription is active
        if (!hasActiveSub) {
            setToolError(strings.SUBSCRIPTION_INACTIVE_ERROR_ADD);
            return;
        }

        // Check tool limit (custom tools count towards the limit)
        if (toolLimit !== 'unlimited' && companyTools.length >= toolLimit) {
            setToolError(`${strings.DASHBOARD_TOOL_LIMIT_REACHED} (${toolLimit})`);
            return;
        }

        // Show confirmation modal
        setShowAddCustomToolConfirm(true);
    };

    const handleImageFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            // Validate file type
            if (!file.type.startsWith('image/')) {
                setToolError(strings.DASHBOARD_SELECT_IMAGE_FILE);
                return;
            }
            
            // Validate file size (max 5MB)
            if (file.size > 5 * 1024 * 1024) {
                setToolError(strings.DASHBOARD_IMAGE_TOO_LARGE);
                return;
            }
            
            setCustomToolImageFile(file);
            setToolError('');
            
            // Create preview
            const reader = new FileReader();
            reader.onloadend = () => {
                setCustomToolImagePreview(reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const cancelAddCustomTool = () => {
        setShowAddCustomToolModal(false);
        setCustomToolName('');
        setCustomToolImageFile(null);
        setCustomToolImagePreview(null);
        setCustomToolColor('#2563EB');
        setCustomToolWithoutMeasurement(false);
        setCustomToolPrecisionClass('');
        setCustomToolMinimumPrecision('');
        setToolError('');
    };

    const handleSaveAllToolColors = async () => {
        const currentUser = auth.currentUser;
        if (!currentUser) {
            setToolError(strings.DASHBOARD_USER_NOT_LOGGED_IN);
            return;
        }

        if (Object.keys(editedToolColors).length === 0) {
            return;
        }

        setIsLoadingTools(true);
        setToolError('');

        try {
            // Save all changed tool colors
            const toolPromises = Object.entries(editedToolColors).map(([toolId, newColor]) =>
                updateToolColor(currentUser.uid, toolId, newColor)
            );
            
            await Promise.all(toolPromises);
            
            // Reload company profile to get updated tools
            const updatedProfile = await getCompanyProfile(currentUser.uid);
            if (updatedProfile) {
                setCompanyTools(updatedProfile.tools || []);
            }
            
            // Clear all edited colors
            setEditedToolColors({});
            setHasColorChanges(false);
            
            logger.log(`All tool colors updated successfully`);
        } catch (error: any) {
            logger.error('Error updating tool colors:', error);
            setToolError(error.message || strings.DASHBOARD_ADD_CUSTOM_TOOL_ERROR);
        } finally {
            setIsLoadingTools(false);
        }
    };

    const handleUpdateMinPrecision = async (toolId: string, minPrecision: string | undefined) => {
        const currentUser = auth.currentUser;
        if (!currentUser) {
            setToolError(strings.DASHBOARD_USER_NOT_LOGGED_IN);
            return;
        }

        setIsLoadingTools(true);
        setToolError('');

        try {
            await updateToolMinPrecision(currentUser.uid, toolId, minPrecision);
            
            // Reload company profile to get updated tools
            const updatedProfile = await getCompanyProfile(currentUser.uid);
            if (updatedProfile) {
                setCompanyTools(updatedProfile.tools || []);
            }
            
            setOpenMinPrecisionDropdown(null);
            logger.log(`Tool minimum precision updated successfully`);
        } catch (error: any) {
            logger.error('Error updating tool minimum precision:', error);
            setToolError(error.message || strings.DASHBOARD_ADD_CUSTOM_TOOL_ERROR);
        } finally {
            setIsLoadingTools(false);
        }
    };

    const handleCancelAddCustomToolConfirm = () => {
        setShowAddCustomToolConfirm(false);
    };

    const handleConfirmAddCustomTool = async () => {
        const currentUser = auth.currentUser;
        if (!currentUser) {
            setToolError(strings.DASHBOARD_USER_NOT_LOGGED_IN);
            setShowAddCustomToolConfirm(false);
            return;
        }

        setIsLoadingTools(true);
        setToolError('');
        setShowAddCustomToolConfirm(false);

        try {
            await addCustomToolToCompany(
                currentUser.uid, 
                customToolName.trim(),
                customToolImageFile || null,
                null, // No external URL when file is uploaded
                customToolColor,
                customToolWithoutMeasurement,
                customToolPrecisionClass,
                customToolMinimumPrecision || undefined
            );
            
            // Reload company profile to get updated tools
            const updatedProfile = await getCompanyProfile(currentUser.uid);
            if (updatedProfile) {
                setCompanyTools(updatedProfile.tools || []);
            }
            
            // Reset form
            setCustomToolName('');
            setCustomToolImageFile(null);
            setCustomToolImagePreview(null);
            setCustomToolColor('#2563EB');
            setCustomToolWithoutMeasurement(false);
            setCustomToolPrecisionClass('');
            setCustomToolMinimumPrecision('');
            setShowAddCustomToolModal(false);
            
            logger.log(`Custom tool "${customToolName}" added to company`);
        } catch (error: any) {
            logger.error('Error adding custom tool to company:', error);
            setToolError(error.message || strings.DASHBOARD_ADD_CUSTOM_TOOL_ERROR);
        } finally {
            setIsLoadingTools(false);
        }
    };

    const handleDeleteTool = (tool: Tool) => {
        setDeletingTool({ id: tool.id, name: tool.type === 'standard' && tool.toolNumber 
            ? getToolName(tool.toolNumber) 
            : tool.name });
        setShowDeleteToolConfirm(true);
    };

    const handleCancelDeleteTool = () => {
        setShowDeleteToolConfirm(false);
        setDeletingTool(null);
    };

    const handleConfirmDeleteTool = async () => {
        if (!deletingTool) return;

        const currentUser = auth.currentUser;
        if (!currentUser || !currentUser.email) {
            logger.error('Cannot delete tool: missing email or user');
            setShowDeleteToolConfirm(false);
            setDeletingTool(null);
            return;
        }

        setIsProcessingToolPayment(true);
        setShowDeleteToolConfirm(false);

        try {
            const successUrl = `${window.location.origin}/dashboard?session_id={CHECKOUT_SESSION_ID}&action=remove_single_tool&toolId=${deletingTool.id}`;
            const cancelUrl = `${window.location.origin}/dashboard`;

            // Create checkout session for remove single tool action
            const response = await createCheckoutSession({
                planId: 'remove_single_tool', // Special identifier
                customerEmail: currentUser.email,
                successUrl,
                cancelUrl,
                metadata: {
                    action: 'remove_single_tool',
                    toolId: deletingTool.id,
                    userId: currentUser.uid
                }
            });

            if (response.url) {
                logger.log('[Dashboard] Redirecting to Stripe checkout for remove single tool');
                window.location.href = response.url;
            } else {
                throw new Error('No checkout URL received from server');
            }
        } catch (error: any) {
            logger.error('[Dashboard] Error creating checkout session for remove tool:', error);
            setToolError(error.message || strings.DASHBOARD_REMOVE_TOOL_ERROR);
            setIsProcessingToolPayment(false);
            setDeletingTool(null);
        }
    };

    // Render loading state
    if (isLoadingRole) {
        return <EngineeringLoadingScreen ariaLabel={strings.LOADING} />;
    }

    // Render admin/manager dashboard interface
    return (
        <>
            <div className="container css-test-loaded">
                <UserProfile displayName={userDisplayName} />
                <button className="logout-btn" onClick={onLogout}>
                    {strings.DASHBOARD_EXIT}
                </button>
                <div className="admin-panel">
                    <h1 className="dashboard-main-title">QUALITY DATUM</h1>
                    
                    {/* Subscription Status Warning */}
                    {!hasActiveSub && (
                        <div className="subscription-warning">
                            <div className="warning-icon">⚠️</div>
                            <div className="warning-content">
                                <h3>{strings.SUBSCRIPTION_INACTIVE_TITLE}</h3>
                                <p>{strings.SUBSCRIPTION_INACTIVE_MESSAGE}</p>
                            </div>
                        </div>
                    )}
                    
                    <div className="company-name-above-search">
                        {companyName || 'Teie ettevõte'}
                    </div>
                    
                    <div className="admin-search-container">
                        <input
                            type="text"
                            className={`admin-search-input${emailError ? ' input-error' : ''}`}
                            placeholder={strings.DASHBOARD_ENTER_EMAIL_PLACEHOLDER}
                            value={searchEmail}
                            onChange={handleEmailInputChange}
                            onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleAddEmail();
                                }
                            }}
                            disabled={isSendingInvitation || !hasActiveSub}
                        />
                        <button
                            className="admin-add-button"
                            onClick={handleAddEmail}
                            disabled={isSendingInvitation || currentUsers >= userLimit || !hasActiveSub}
                            title={!hasActiveSub ? strings.SUBSCRIPTION_INACTIVE_TITLE : currentUsers >= userLimit ? strings.DASHBOARD_USER_LIMIT_REACHED : ''}
                        >
                            {isSendingInvitation ? (
                                strings.DASHBOARD_SENDING
                            ) : (
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="12" y1="5" x2="12" y2="19"></line>
                                    <line x1="5" y1="12" x2="19" y2="12"></line>
                                </svg>
                            )}
                        </button>
                    </div>
                    
                    <div className="subscription-counters">
                        <div className="counter-item">
                            <span className="counter-label">{strings.DASHBOARD_EDITORS_LABEL}</span>
                            <span className={`counter-value ${currentEditors >= editorLimit ? 'limit-reached' : ''}`}>
                                {currentEditors} / {editorLimit}
                            </span>
                        </div>
                        <div className="counter-item">
                            <span className="counter-label">{strings.DASHBOARD_USERS_LABEL}</span>
                            <span className={`counter-value ${currentUsers >= userLimit ? 'limit-reached' : ''}`}>
                                {currentUsers} / {userLimit}
                            </span>
                        </div>
                        <div className="counter-item">
                            <span className="counter-label">{strings.DASHBOARD_TOOLS_LABEL}</span>
                            <span className={`counter-value ${toolLimit !== 'unlimited' && companyTools.length >= toolLimit ? 'limit-reached' : ''}`}>
                                {companyTools.length} / {toolLimit === 'unlimited' ? strings.UNLIMITED_LABEL : toolLimit}
                            </span>
                        </div>
                    </div>
                    
                    {emailError && <div className="email-error-message">{emailError}</div>}
                    {emailSuccess && <div className="email-success-message">{emailSuccess}</div>}
                    {toolError && <div className="email-error-message">{toolError}</div>}
                    
                    {/* Company Members List */}
                    <div className="company-members-section">
                        <h3 className="members-title">{strings.DASHBOARD_MEMBERS_TITLE}</h3>
                        {isLoadingMembers ? (
                            <div className="loading-members">{strings.DASHBOARD_LOADING}</div>
                        ) : companyMembers.length > 0 ? (
                            <div className="members-list">
                                {companyMembers.map((member) => {
                                    const isEditing = editingMember === member.uid;
                                    const currentRole = isEditing ? editedRoles[member.uid] : member.role;
                                    
                                    return (
                                        <div key={member.uid} className="member-item">
                                            <div className="member-info">
                                                <span className="member-email">{member.email}</span>
                                            </div>
                                            
                                            <div className="member-actions">
                                                <div className="member-role-selector">
                                                    <div 
                                                        className={`role-box ${currentRole === 'user' ? 'selected' : ''} ${member.role === 'user' && !isEditing ? 'from-db' : ''}`}
                                                        onClick={() => {
                                                            if (!isEditing) {
                                                                handleRoleEdit(member.uid, member.role);
                                                            }
                                                            handleRoleChange(member.uid, 'user');
                                                        }}
                                                    >
                                                        {strings.DASHBOARD_USER_ROLE}
                                                    </div>
                                                    <div 
                                                        className={`role-box ${currentRole === 'editor' ? 'selected' : ''} ${member.role === 'editor' && !isEditing ? 'from-db' : ''}`}
                                                        onClick={() => {
                                                            if (!isEditing) {
                                                                handleRoleEdit(member.uid, member.role);
                                                            }
                                                            handleRoleChange(member.uid, 'editor');
                                                        }}
                                                    >
                                                        {strings.DASHBOARD_EDITOR_ROLE}
                                                    </div>
                                                </div>
                                                
                                                <div className="member-action-buttons">
                                                    <button
                                                        className="member-action-btn save-btn"
                                                        onClick={() => handleSaveRole(member.uid)}
                                                        title={strings.DASHBOARD_SAVE}
                                                        disabled={!isEditing}
                                                        style={{ opacity: isEditing ? 1 : 0.3 }}
                                                    >
                                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                            <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
                                                            <polyline points="17 21 17 13 7 13 7 21"></polyline>
                                                            <polyline points="7 3 7 8 15 8"></polyline>
                                                        </svg>
                                                    </button>
                                                    
                                                    <button
                                                        className="member-action-btn delete-btn"
                                                        onClick={() => handleDeleteMember(member.uid)}
                                                        title={strings.DASHBOARD_DELETE}
                                                    >
                                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                            <polyline points="3 6 5 6 21 6"></polyline>
                                                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                                            <line x1="10" y1="11" x2="10" y2="17"></line>
                                                            <line x1="14" y1="11" x2="14" y2="17"></line>
                                                        </svg>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="no-members">{strings.DASHBOARD_NO_MEMBERS}</div>
                        )}
                    </div>

                    {/* Company Tools List */}
                    <div className="company-tools-section">
                        <div className="tools-section-header">
                            <div className="tools-title-wrapper">
                                <h3 className="tools-title">{strings.DASHBOARD_TOOLS_TITLE}</h3>
                                {Object.keys(editedToolColors).length > 0 && (
                                    <button
                                        type="button"
                                        className="save-all-colors-btn"
                                        onClick={handleSaveAllToolColors}
                                        disabled={isLoadingTools}
                                        title={strings.DASHBOARD_SAVE || 'Save'}
                                    >
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
                                            <polyline points="17 21 17 13 7 13 7 21"></polyline>
                                            <polyline points="7 3 7 8 15 8"></polyline>
                                        </svg>
                                    </button>
                                )}
                            </div>
                            <div className="tools-add-container">
                                {toolError && <div className="tool-error-message">{toolError}</div>}
                                {showAddToolDropdown && (
                                    <div className="tools-dropdown">
                                        {getAvailableTools().length > 0 ? (
                                            getAvailableTools().map((toolNumber) => (
                                                <div
                                                    key={toolNumber}
                                                    className="tool-dropdown-item"
                                                    onClick={() => handleAddTool(toolNumber)}
                                                >
                                                    <img
                                                        src={getToolImagePath(toolNumber)}
                                                        alt={getToolName(toolNumber)}
                                                        className="tool-dropdown-image"
                                                    />
                                                    <span>{getToolName(toolNumber)}</span>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="tool-dropdown-empty">
                                                {strings.DASHBOARD_ALL_TOOLS_ADDED}
                                            </div>
                                        )}
                                    </div>
                                )}
                                <button
                                    className="add-tool-button"
                                    onClick={() => {
                                        setShowAddToolDropdown(!showAddToolDropdown);
                                        setToolError('');
                                    }}
                                    disabled={isLoadingTools || !hasActiveSub || (toolLimit !== 'unlimited' && companyTools.length >= toolLimit)}
                                    title={!hasActiveSub ? strings.SUBSCRIPTION_INACTIVE_TITLE : (toolLimit !== 'unlimited' && companyTools.length >= toolLimit) ? strings.DASHBOARD_TOOL_LIMIT_REACHED : strings.DASHBOARD_ADD_TOOL}
                                >
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <line x1="12" y1="5" x2="12" y2="19"></line>
                                        <line x1="5" y1="12" x2="19" y2="12"></line>
                                    </svg>
                                </button>
                                <button
                                    className="add-tool-button add-custom-tool-button"
                                    onClick={() => {
                                        setShowAddCustomToolModal(true);
                                        setCustomToolName('');
                                        setCustomToolImageFile(null);
                                        setCustomToolImagePreview(null);
                                        setCustomToolColor('#2563EB');
                                        setCustomToolWithoutMeasurement(false);
                                        setCustomToolPrecisionClass('');
                                        setCustomToolMinimumPrecision('');
                                        setToolError('');
                                    }}
                                    disabled={isLoadingTools || !hasActiveSub || (toolLimit !== 'unlimited' && companyTools.length >= toolLimit)}
                                    title={!hasActiveSub ? strings.SUBSCRIPTION_INACTIVE_TITLE : (toolLimit !== 'unlimited' && companyTools.length >= toolLimit) ? strings.DASHBOARD_TOOL_LIMIT_REACHED : strings.DASHBOARD_ADD_CUSTOM_TOOL}
                                >
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <line x1="12" y1="5" x2="12" y2="19"></line>
                                        <line x1="5" y1="12" x2="19" y2="12"></line>
                                    </svg>
                                    {strings.DASHBOARD_ADD_CUSTOM_TOOL}
                                </button>
                            </div>
                        </div>
                        {isLoadingTools ? (
                            <div className="loading-tools">{strings.LOADING}</div>
                        ) : companyTools.length > 0 ? (
                            <div className="tools-list">
                                {companyTools.map((tool, index) => {
                                    // Number tools sequentially starting from 1
                                    const sequentialNumber = index + 1;
                                    const editedColor = editedToolColors[tool.id];
                                    const toolColor = editedColor || tool.color;
                                    return (
                                        <div key={tool.id} className="tool-card">
                                            <div className="tool-card-header">
                                                <div className="tool-badge-wrapper">
                                                    <input
                                                        type="color"
                                                        id={`tool-color-${tool.id}`}
                                                        value={toolColor}
                                                        onChange={(e) => {
                                                            const newColor = e.target.value;
                                                            setEditedToolColors(prev => ({
                                                                ...prev,
                                                                [tool.id]: newColor
                                                            }));
                                                            setHasColorChanges(true);
                                                        }}
                                                        className="tool-color-picker-input"
                                                    />
                                                    <div 
                                                        className="tool-number-badge tool-number-badge-clickable" 
                                                        style={{ backgroundColor: addAlphaToHex(toolColor, 0.53) }}
                                                        onClick={() => document.getElementById(`tool-color-${tool.id}`)?.click()}
                                                    >
                                                        {sequentialNumber}
                                                    </div>
                                                </div>
                                                {tool.precisionClass && (
                                                    <div className="tool-precision-class">
                                                        {tool.precisionClass}
                                                    </div>
                                                )}
                                                <button
                                                    className="tool-delete-button"
                                                    onClick={() => handleDeleteTool(tool)}
                                                    title={strings.DASHBOARD_DELETE_TOOL || 'Delete Tool'}
                                                    disabled={isProcessingToolPayment}
                                                >
                                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <polyline points="3 6 5 6 21 6"></polyline>
                                                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                                        <line x1="10" y1="11" x2="10" y2="17"></line>
                                                        <line x1="14" y1="11" x2="14" y2="17"></line>
                                                    </svg>
                                                </button>
                                            </div>
                                            <div className="tool-card-image">
                                                {tool.imageUrl ? (
                                                    <img 
                                                        src={tool.imageUrl} 
                                                        alt={tool.type === 'standard' && tool.toolNumber 
                                                            ? getToolName(tool.toolNumber) 
                                                            : tool.name}
                                                        className="tool-image"
                                                    />
                                                ) : (
                                                    <div className="tool-image-placeholder">
                                                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                                                            <line x1="9" y1="3" x2="9" y2="21"></line>
                                                        </svg>
                                                    </div>
                                                )}
                                                {!tool.toolWithoutMeasurement && (
                                                    <div className="tool-min-precision">
                                                        m.p  {tool.minToolPrecision || '0'}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="tool-card-footer">
                                                <span className="tool-name">
                                                    {tool.type === 'standard' && tool.toolNumber 
                                                        ? getToolName(tool.toolNumber) 
                                                        : tool.name}
                                                </span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="no-tools">Ettevõttel pole veel mõõteriistu</div>
                        )}
                    </div>
                </div>
            </div>

            {/* Add Custom Tool Modal */}
            {showAddCustomToolModal && (
                <div className="modal-overlay">
                    <div className="modal-container">
                        <div className="modal-header">
                            <h2>{strings.DASHBOARD_ADD_CUSTOM_TOOL_TITLE}</h2>
                        </div>

                        <div className="modal-body">
                            <div className="form-group">
                                <label htmlFor="customToolName">{strings.DASHBOARD_CUSTOM_TOOL_NAME_LABEL}</label>
                                <input
                                    id="customToolName"
                                    type="text"
                                    className="form-input"
                                    placeholder={strings.DASHBOARD_CUSTOM_TOOL_NAME_PLACEHOLDER}
                                    value={customToolName}
                                    onChange={(e) => setCustomToolName(e.target.value)}
                                    onKeyPress={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            handleAddCustomTool();
                                        }
                                    }}
                                    required
                                />
                                <div className="checkbox-wrapper">
                                    <label htmlFor="customToolWithoutMeasurement" className="checkbox-title">
                                        {strings.DASHBOARD_TOOL_WITHOUT_MEASUREMENT}
                                    </label>
                                    <div className="checkbox-row">
                                        <input
                                            type="checkbox"
                                            id="customToolWithoutMeasurement"
                                            checked={customToolWithoutMeasurement}
                                            onChange={(e) => {
                                                const isChecked = e.target.checked;
                                                setCustomToolWithoutMeasurement(isChecked);
                                                // Clear precision class if it doesn't match new validation rules
                                                if (isChecked) {
                                                    // If checked (tool without measurement), clear if contains numbers
                                                    if (customToolPrecisionClass && /[\d]/.test(customToolPrecisionClass)) {
                                                        setCustomToolPrecisionClass('');
                                                    }
                                                } else {
                                                    // If unchecked (tool with measurement), clear if contains non-numeric characters (except dots and commas)
                                                    if (customToolPrecisionClass && /[^\d.,]/.test(customToolPrecisionClass)) {
                                                        setCustomToolPrecisionClass('');
                                                    }
                                                }
                                            }}
                                        />
                                        <span className="form-help-text">{strings.DASHBOARD_TOOL_WITHOUT_MEASUREMENT_DESCRIPTION}</span>
                                    </div>
                                </div>
                            </div>
                            <div className="form-group">
                                <label htmlFor="customToolPrecisionClass">{strings.DASHBOARD_PRECISION_CLASS_LABEL}</label>
                                <input
                                    id="customToolPrecisionClass"
                                    type="text"
                                    className="form-input"
                                    placeholder={strings.DASHBOARD_PRECISION_CLASS_PLACEHOLDER}
                                    value={customToolPrecisionClass}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        if (customToolWithoutMeasurement) {
                                            // Tool without measurement: only text allowed (letters, spaces, hyphens, etc., but no numbers)
                                            // Allow letters, spaces, hyphens, underscores, and other common text characters
                                            if (value === '' || /^[a-zA-Zа-яА-ЯёЁ\s\-_.,;:!?'"()]+$/.test(value)) {
                                                setCustomToolPrecisionClass(value);
                                            }
                                        } else {
                                            // Tool with measurement: only numbers, dots and commas allowed
                                            if (value === '' || /^[\d.,]+$/.test(value)) {
                                                setCustomToolPrecisionClass(value);
                                            }
                                        }
                                    }}
                                    onKeyPress={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            handleAddCustomTool();
                                        }
                                    }}
                                    required
                                />
                                <p className="form-help-text">{strings.DASHBOARD_PRECISION_CLASS_HELP}</p>
                            </div>
                            {!customToolWithoutMeasurement && (
                                <div className="form-group">
                                    <label>{strings.DASHBOARD_MINIMUM_PRECISION_LABEL}</label>
                                    <div className="minimum-precision-buttons">
                                        <button
                                            type="button"
                                            className={`precision-button ${customToolMinimumPrecision === '0' ? 'active' : ''}`}
                                            onClick={() => setCustomToolMinimumPrecision('0')}
                                        >
                                            0
                                        </button>
                                        <button
                                            type="button"
                                            className={`precision-button ${customToolMinimumPrecision === '0.0' ? 'active' : ''}`}
                                            onClick={() => setCustomToolMinimumPrecision('0.0')}
                                        >
                                            0.0
                                        </button>
                                        <button
                                            type="button"
                                            className={`precision-button ${customToolMinimumPrecision === '0.00' ? 'active' : ''}`}
                                            onClick={() => setCustomToolMinimumPrecision('0.00')}
                                        >
                                            0.00
                                        </button>
                                        <button
                                            type="button"
                                            className={`precision-button ${customToolMinimumPrecision === '0.000' ? 'active' : ''}`}
                                            onClick={() => setCustomToolMinimumPrecision('0.000')}
                                        >
                                            0.000
                                        </button>
                                    </div>
                                    <p className="form-help-text">{strings.DASHBOARD_MINIMUM_PRECISION_HELP}</p>
                                </div>
                            )}
                            <div className="form-group">
                                <label htmlFor="customToolColor">{strings.DASHBOARD_CUSTOM_TOOL_COLOR_LABEL}</label>
                                <div className="color-picker-wrapper">
                                    <input
                                        id="customToolColor"
                                        type="color"
                                        value={customToolColor || '#2563EB'}
                                        onChange={(e) => setCustomToolColor(e.target.value)}
                                        className="color-picker-input"
                                        required
                                    />
                                    <button
                                        type="button"
                                        onClick={() => document.getElementById('customToolColor')?.click()}
                                        className="color-picker-button"
                                    >
                                        <span className="color-picker-preview" style={{ backgroundColor: addAlphaToHex(customToolColor || '#2563EB', 0.53) }}>
                                            {companyTools.length + 1}
                                        </span>
                                        <span className="color-picker-text">{customToolColor ? customToolColor.toUpperCase() : strings.DASHBOARD_SELECT_COLOR}</span>
                                    </button>
                                </div>
                            </div>
                            <div className="form-group">
                                <label>{strings.DASHBOARD_IMAGE_FILE_LABEL}</label>
                                <p className="form-note-text">{strings.DASHBOARD_IMAGE_FILE_NOTE}</p>
                                <div className="file-input-wrapper">
                                    <input
                                        id="customToolImageFile"
                                        type="file"
                                        accept="image/*"
                                        className="form-input"
                                        onChange={handleImageFileChange}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => document.getElementById('customToolImageFile')?.click()}
                                        className="file-input-label"
                                    >
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                            <polyline points="17 8 12 3 7 8"></polyline>
                                            <line x1="12" y1="3" x2="12" y2="15"></line>
                                        </svg>
                                        {customToolImageFile ? customToolImageFile.name : strings.DASHBOARD_SELECT_FILE}
                                    </button>
                                </div>
                                {customToolImagePreview && (
                                    <div className="custom-tool-image-preview">
                                        <img 
                                            src={customToolImagePreview} 
                                            alt={strings.DASHBOARD_PREVIEW} 
                                            className="preview-image"
                                        />
                                        <button
                                            type="button"
                                            className="remove-preview-btn"
                                            onClick={() => {
                                                setCustomToolImageFile(null);
                                                setCustomToolImagePreview(null);
                                                const fileInput = document.getElementById('customToolImageFile') as HTMLInputElement;
                                                if (fileInput) {
                                                    fileInput.value = '';
                                                }
                                            }}
                                        >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                                <line x1="6" y1="6" x2="18" y2="18"></line>
                                            </svg>
                                        </button>
                                    </div>
                                )}
                                <p className="form-help-text">
                                    {strings.DASHBOARD_IMAGE_FILE_HELP}
                                </p>
                            </div>
                        </div>

                        <div className="modal-footer">
                            <button className="btn-secondary" onClick={cancelAddCustomTool}>
                                {strings.DASHBOARD_CANCEL}
                            </button>
                            <button 
                                className="btn-primary" 
                                onClick={handleAddCustomTool}
                                disabled={isLoadingTools || !customToolName.trim() || !customToolColor || !customToolImageFile || !customToolPrecisionClass.trim()}
                            >
                                {isLoadingTools ? strings.DASHBOARD_SAVING : strings.DASHBOARD_ADD}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add Custom Tool Confirmation Modal */}
            {showAddCustomToolConfirm && (
                <div className="modal-overlay">
                    <div className="modal-container delete-modal">
                        <div className="modal-header">
                            <h2>{strings.ADD_TOOL_CONFIRM_TITLE}</h2>
                        </div>
                        <div className="modal-body">
                            <p className="delete-confirmation-text">
                                {strings.ADD_TOOL_CONFIRM_MESSAGE}
                            </p>
                            <p className="delete-warning-text">
                                {strings.ADD_TOOL_WARNING}
                            </p>
                        </div>
                        <div className="modal-footer">
                            <button 
                                className="btn-secondary" 
                                onClick={handleCancelAddCustomToolConfirm}
                            >
                                {strings.DASHBOARD_CANCEL}
                            </button>
                            <button 
                                className="btn-primary" 
                                onClick={handleConfirmAddCustomTool}
                                disabled={isLoadingTools}
                            >
                                {isLoadingTools ? strings.DASHBOARD_SAVING : strings.ADD_TOOL_CONFIRM}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add Tool Confirmation Modal */}
            {showAddToolConfirm && pendingToolNumber !== null && (
                <div className="modal-overlay">
                    <div className="modal-container delete-modal">
                        <div className="modal-header">
                            <h2>{strings.ADD_TOOL_CONFIRM_TITLE}</h2>
                        </div>
                        <div className="modal-body">
                            <p className="delete-confirmation-text">
                                {strings.ADD_TOOL_CONFIRM_MESSAGE}
                            </p>
                            <p className="delete-warning-text">
                                {strings.ADD_TOOL_WARNING}
                            </p>
                            <div className="form-group" style={{ marginTop: '20px' }}>
                                <label htmlFor="standardToolPrecisionClass">{strings.DASHBOARD_PRECISION_CLASS_LABEL}</label>
                                <input
                                    id="standardToolPrecisionClass"
                                    type="text"
                                    className="form-input"
                                    placeholder={strings.DASHBOARD_PRECISION_CLASS_PLACEHOLDER}
                                    value={standardToolPrecisionClass}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        const toolsWithoutMeasurement = [ 6, 7, 8, 11 ];
                                        const isToolWithoutMeasurement = pendingToolNumber !== null && toolsWithoutMeasurement.includes(pendingToolNumber);
                                        
                                        if (isToolWithoutMeasurement) {
                                            // Tool without measurement: allow any characters including numbers
                                            setStandardToolPrecisionClass(value);
                                        } else {
                                            // Tool with measurement: only numbers, dots and commas allowed
                                            if (value === '' || /^[\d.,]+$/.test(value)) {
                                                setStandardToolPrecisionClass(value);
                                            }
                                        }
                                    }}
                                />
                                {pendingToolNumber !== null && !([ 6, 7, 8, 11 ].includes(pendingToolNumber)) && (
                                    <p className="form-help-text">{strings.DASHBOARD_PRECISION_CLASS_HELP}</p>
                                )}
                                {toolError && (
                                    <p className="error-message" style={{ color: '#DC2626', marginTop: '5px', fontSize: '14px' }}>
                                        {toolError}
                                    </p>
                                )}
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button 
                                className="btn-secondary" 
                                onClick={handleCancelAddTool}
                                disabled={isLoadingTools}
                            >
                                {strings.DASHBOARD_CANCEL}
                            </button>
                            <button 
                                className="btn-delete" 
                                onClick={handleConfirmAddTool}
                                disabled={isLoadingTools}
                            >
                                {isLoadingTools ? strings.DASHBOARD_SAVING : strings.ADD_TOOL_CONFIRM}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {deletingMember && (
                <div className="modal-overlay">
                    <div className="modal-container delete-modal">
                        <div className="modal-header">
                            <h2>{strings.DASHBOARD_CONFIRM_DELETE_TITLE}</h2>
                        </div>

                        <div className="modal-body">
                            <p className="delete-confirmation-text">
                                {strings.DASHBOARD_CONFIRM_DELETE_MESSAGE} <strong>{deletingMember.email}</strong>?
                            </p>
                            <p className="delete-warning-text">
                                {strings.DASHBOARD_DELETE_WARNING}
                            </p>
                        </div>

                        <div className="modal-footer">
                            <button className="btn-secondary" onClick={cancelDeleteMember}>
                                {strings.DASHBOARD_CANCEL}
                            </button>
                            <button className="btn-delete" onClick={confirmDeleteMember}>
                                {strings.DASHBOARD_DELETE}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Tool Confirmation Modal */}
            {showDeleteToolConfirm && deletingTool && (
                <div className="modal-overlay">
                    <div className="modal-container delete-modal">
                        <div className="modal-header">
                            <h2>{strings.DASHBOARD_DELETE_TOOL_CONFIRM_TITLE || 'Confirm Tool Deletion'}</h2>
                        </div>
                        <div className="modal-body">
                            <p className="delete-confirmation-text">
                                {strings.DASHBOARD_DELETE_TOOL_CONFIRM_MESSAGE || 'Are you sure you want to delete the tool'} <strong>"{deletingTool.name}"</strong>?
                            </p>
                            <p className="delete-warning-text">
                                {strings.DASHBOARD_DELETE_TOOL_WARNING || 'This action will cost 1 EUR + tax and will permanently delete this tool (including custom tool images if applicable).'}
                            </p>
                        </div>
                        <div className="modal-footer">
                            <button 
                                className="btn-secondary" 
                                onClick={handleCancelDeleteTool}
                                disabled={isProcessingToolPayment}
                            >
                                {strings.DASHBOARD_CANCEL}
                            </button>
                            <button 
                                className="btn-delete" 
                                onClick={handleConfirmDeleteTool}
                                disabled={isProcessingToolPayment}
                            >
                                {isProcessingToolPayment ? (strings.DASHBOARD_PROCESSING || 'Processing...') : (strings.DASHBOARD_DELETE_TOOL_CONFIRM || 'Confirm & Pay')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
